import { Skeleton } from "@atlant/ui"

import { formatAmount, sortTotals } from "#utils/money.ts"

import type { MonthSummary } from "../../components/month-card.tsx"
import type { LateInvoice } from "../late-invoices.tsx"
import { LateByMonthChart } from "./late-by-month-chart.tsx"

// Sum in integer cents: float addition of decimal strings drifts
const sumByCurrency = (invoices: LateInvoice[]) =>
  Object.entries(
    invoices.reduce<Record<string, number>>((acc, { currency, total }) => {
      acc[currency] = (acc[currency] ?? 0) + Math.round(Number(total) * 100)
      return acc
    }, {}),
  ).map(([currency, cents]) => ({ currency, amount: (cents / 100).toFixed(2) }))

interface LateKpisProps {
  invoices: LateInvoice[]
  isLoading: boolean
  summaries: MonthSummary[]
  summariesLoading: boolean
}

export const LateKpis = ({
  invoices,
  isLoading,
  summaries,
  summariesLoading,
}: LateKpisProps) => {
  const [primary, ...other] = sortTotals(sumByCurrency(invoices))

  return (
    <div className="bg-card flex rounded-lg border">
      <div className="flex min-w-0 flex-1 items-stretch">
        <Kpi
          label="Late invoices"
          isLoading={isLoading}
          value={String(invoices.length)}
        />
        <div className="flex items-center py-2">
          <div className="bg-border h-full w-px" />
        </div>
      </div>
      <div className="flex min-w-0 flex-1 items-stretch">
        <Kpi
          label="Total amount"
          isLoading={isLoading}
          value={
            primary
              ? formatAmount(primary.amount, primary.currency)
              : formatAmount("0", "EUR")
          }
          sub={other.map(({ amount, currency }) => `+ ${formatAmount(amount, currency)}`)}
        />
        <div className="flex items-center py-2">
          <div className="bg-border h-full w-px" />
        </div>
      </div>
      <div className="flex min-w-0 flex-1 flex-col gap-2 p-4">
        <span className="text-muted-foreground text-sm font-medium">
          Late invoices by service month
        </span>
        <LateByMonthChart summaries={summaries} isLoading={summariesLoading} />
      </div>
    </div>
  )
}

interface KpiProps {
  label: string
  value: string
  isLoading: boolean
  sub?: string[]
}

const Kpi = ({ label, value, isLoading, sub }: KpiProps) => (
  <div className="flex flex-1 flex-col gap-1 p-4">
    <span className="text-muted-foreground text-sm font-medium">{label}</span>
    {isLoading ? (
      <Skeleton className="h-8 w-28" />
    ) : (
      <span className="text-primary text-2xl font-semibold tabular-nums">{value}</span>
    )}
    {!isLoading &&
      sub?.map((line) => (
        <span key={line} className="text-muted-foreground text-xs">
          {line}
        </span>
      ))}
  </div>
)
